import React, { useState, Children } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckIcon, XIcon, ShieldIcon, FileTextIcon, CreditCardIcon, MessageSquareIcon, BookOpenIcon, ChevronDownIcon, LockIcon, XCircleIcon } from 'lucide-react';
// ─── Animation Variants ───────────────────────────────────────────────────────
const fadeUp = {
  hidden: {
    opacity: 0,
    y: 32
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: 'easeOut'
    }
  }
};
const staggerContainer = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.12
    }
  }
};
// ─── CTA Button ───────────────────────────────────────────────────────────────
function CtaButton({
  variant = 'primary'
}: {
  variant?: 'primary' | 'inverse';
}) {
  const base = 'inline-block px-8 py-4 rounded-full text-sm font-bold tracking-widest uppercase transition-transform duration-200 hover:scale-105 active:scale-95 cursor-pointer text-center shadow-lg';
  const styles = variant === 'primary' ? 'bg-[#C4704A] text-[#FDF8F3] hover:bg-[#A0522D]' : 'bg-[#FDF8F3] text-[#C4704A] hover:bg-white';
  const handleClick = () => {
    window.location.href = 'https://canaimacreative.com/cart/44239010267229:1';
  };
  return <button className={`${base} ${styles}`} style={{
    fontFamily: 'var(--font-body)'
  }} onClick={handleClick} data-id="element-0">
      Sí, Quiero Recuperar el Control Ahora
    </button>;
}
// ─── Section Wrapper ──────────────────────────────────────────────────────────
function Section({
  children,
  className = ''
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return <section className={`w-full px-5 py-16 md:py-24 ${className}`} data-id="element-1">
      <div className="max-w-5xl mx-auto" data-id="element-2">{children}</div>
    </section>;
}
// ─── Animated Section ─────────────────────────────────────────────────────────
function AnimSection({
  children,
  className = '',
  id,
  style
}: {
  children: React.ReactNode;
  className?: string;
  id?: string;
  style?: React.CSSProperties;
}) {
  return <motion.section id={id} className={`${`w-full px-5 py-16 md:py-24 ${className}`} bg-[#F9F4EF]`} style={style} initial="hidden" whileInView="visible" viewport={{
    once: true,
    amount: 0.15
  }} variants={staggerContainer} data-id="element-3">
      <div className="max-w-5xl mx-auto" data-id="element-4">{children}</div>
    </motion.section>;
}
// ─── Section Label ────────────────────────────────────────────────────────────
function SectionLabel({
  children
}: {
  children: React.ReactNode;
}) {
  return <p className="text-xs font-bold tracking-[0.25em] uppercase mb-4" style={{
    color: 'var(--color-primary)',
    fontFamily: 'var(--font-body)'
  }} data-id="element-5">
      {children}
    </p>;
}
// ─── 1. HERO ──────────────────────────────────────────────────────────────────
function HeroSection() {
  return <section className="w-full px-5 pt-16 pb-0 md:pt-20" style={{
    backgroundColor: 'var(--color-bg)'
  }} data-id="element-6">
      <div className="max-w-5xl mx-auto" data-id="element-7">
        <div className="flex flex-col md:flex-row items-center gap-10 md:gap-16" data-id="element-8">
          {/* Left */}
          <motion.div className="flex-1 flex flex-col gap-6" initial="hidden" animate="visible" variants={staggerContainer} data-id="element-9">
            <motion.div variants={fadeUp} data-id="element-10">
              <span className="inline-block text-xs font-bold tracking-[0.25em] uppercase px-3 py-1 rounded-full mb-4" style={{
              backgroundColor: '#F5E6DC',
              color: 'var(--color-primary)',
              fontFamily: 'var(--font-body)'
            }} data-id="element-11">
                Protocolo de 30 Días
              </span>
            </motion.div>

            <motion.h1 variants={fadeUp} className="text-3xl md:text-4xl lg:text-5xl font-black leading-tight" style={{
            fontFamily: 'var(--font-heading)',
            color: 'var(--color-text)'
          }} data-id="element-12">
              Guía de Supervivencia Post-Divorcio:{' '}
              <em className="not-italic" style={{
              color: 'var(--color-primary)'
            }} data-id="element-13">
                El Protocolo de 30 Días para Recuperar el Control
              </em>
            </motion.h1>

            <motion.p variants={fadeUp} className="text-lg font-bold leading-snug" style={{
            color: 'var(--color-text-muted)',
            fontFamily: 'var(--font-body)'
          }} data-id="element-14">
              Protege tus finanzas, asegura tu estatus legal en EE.&nbsp;UU. y
              detén el caos emocional antes de que el proceso te consuma.
            </motion.p>

            <motion.p variants={fadeUp} className="text-base leading-relaxed" style={{
            color: 'var(--color-text)',
            fontFamily: 'var(--font-body)'
          }} data-id="element-15">
              Estás en la cocina, el silencio después de la discusión es
              ensordecedor y tienes una pila de papeles en inglés que no
              terminas de entender. La pregunta no es si duele, la pregunta es:{' '}
              <strong data-id="element-16">
                ¿Qué voy a hacer mañana para que no me quiten lo que es mío?
              </strong>{' '}
              El divorcio en este país no es solo una ruptura; es una batalla
              logística donde la que mejor se prepara es la que sobrevive.
            </motion.p>

            <motion.div variants={fadeUp} data-id="element-17">
              <CtaButton variant="primary" data-id="element-18" />
            </motion.div>
          </motion.div>

          {/* Right — Image */}
          <motion.div className="flex-1 w-full max-w-sm md:max-w-none self-end" initial={{
          opacity: 0,
          x: 40
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          duration: 0.8,
          ease: 'easeOut',
          delay: 0.2
        }} data-id="element-19">
            <img src="https://cdn.magicpatterns.com/uploads/1QQygaQPKX5Jhcg6Y5rhYe/jjjj.png" alt="Mujer latina sosteniendo una tablet con la Guía de Supervivencia Post-Divorcio" className="w-full h-auto object-cover rounded-t-2xl md:rounded-2xl shadow-2xl" style={{
            maxHeight: '560px',
            objectPosition: 'top center'
          }} data-id="element-20" />
          </motion.div>
        </div>
      </div>
    </section>;
}
// ─── 2. PROBLEM ───────────────────────────────────────────────────────────────
function ProblemSection() {
  return <AnimSection style={{
    backgroundColor: 'var(--color-bg-dark)'
  } as React.CSSProperties} data-id="element-21">
      <div className="border-l-4 pl-8 py-2" style={{
      borderColor: 'var(--color-primary)'
    }} data-id="element-22">
        <motion.div variants={fadeUp} data-id="element-23">
          <SectionLabel data-id="element-24">El Problema</SectionLabel>
        </motion.div>
        <motion.h2 variants={fadeUp} className="text-2xl md:text-3xl lg:text-4xl font-black leading-tight mb-6" style={{
        fontFamily: 'var(--font-heading)',
        color: '#FDF8F3'
      }} data-id="element-25">
          La mayoría de las mujeres latinas en EE.&nbsp;UU. pierden miles de
          dólares, dañan su historial de crédito y arriesgan la estabilidad de
          sus hijos por una sola razón:{' '}
          <span style={{
          color: 'var(--color-accent)'
        }} data-id="element-26">
            esperar a que "las aguas se calmen" para actuar.
          </span>
        </motion.h2>
        <motion.p variants={fadeUp} className="text-base md:text-lg leading-relaxed" style={{
        color: '#D4B8AC',
        fontFamily: 'var(--font-body)'
      }} data-id="element-27">
          Mientras tú sufres, el sistema sigue avanzando. Si no proteges tus
          cuentas bancarias, tu historial crediticio y tu estatus hoy mismo,
          podrías pasar los próximos{' '}
          <strong style={{
          color: '#FDF8F3'
        }} data-id="element-28">
            5 años
          </strong>{' '}
          tratando de reparar errores cometidos en los primeros{' '}
          <strong style={{
          color: '#FDF8F3'
        }} data-id="element-29">
            15 días
          </strong>
          .
        </motion.p>
      </div>
    </AnimSection>;
}
// ─── 3. WHY NOTHING WORKED ────────────────────────────────────────────────────
function WhyNotWorkedSection() {
  return <AnimSection style={{
    backgroundColor: 'var(--color-bg)'
  } as React.CSSProperties} data-id="element-30">
      <motion.div variants={fadeUp} data-id="element-31">
        <SectionLabel data-id="element-32">Por qué fallaron las otras opciones</SectionLabel>
      </motion.div>
      <motion.h2 variants={fadeUp} className="text-2xl md:text-3xl font-black mb-10" style={{
      fontFamily: 'var(--font-heading)',
      color: 'var(--color-text)'
    }} data-id="element-33">
        Por qué lo que intentaste no funcionó
      </motion.h2>
      <motion.div className="flex flex-col gap-5" variants={staggerContainer} data-id="element-34">
        {['Escuchar consejos de amigas que solo te dicen «échale ganas» no paga la renta.', 'Esperar a que tu abogado te explique todo te costará $300 dólares la hora de consulta.', 'Buscar en Google te genera una parálisis por análisis con leyes que ni siquiera aplican a tu estado.', 'Confiar en la «buena voluntad» de tu ex es el error más caro que puedes cometer.'].map((item, i) => <motion.div key={i} variants={fadeUp} className="flex items-start gap-4 p-5 rounded-xl" style={{
        backgroundColor: '#F5EDE6'
      }} data-id="element-35">
            <span className="flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center mt-0.5" style={{
          backgroundColor: '#FDDDD0'
        }} data-id="element-36">
              <XIcon className="w-4 h-4" style={{
            color: 'var(--color-primary)'
          }} data-id="element-37" />
            </span>
            <p className="text-base leading-relaxed" style={{
          color: 'var(--color-text)',
          fontFamily: 'var(--font-body)'
        }} data-id="element-38">
              {item}
            </p>
          </motion.div>)}
      </motion.div>
    </AnimSection>;
}
// ─── 4. MECHANISM ─────────────────────────────────────────────────────────────
function MechanismSection() {
  return <AnimSection style={{
    backgroundColor: 'var(--color-primary)'
  } as React.CSSProperties} data-id="element-39">
      <div className="text-center max-w-3xl mx-auto" data-id="element-40">
        <motion.div variants={fadeUp} data-id="element-41">
          <span className="inline-block text-xs font-bold tracking-[0.25em] uppercase px-3 py-1 rounded-full mb-6" style={{
          backgroundColor: 'rgba(255,255,255,0.2)',
          color: '#FDF8F3',
          fontFamily: 'var(--font-body)'
        }} data-id="element-42">
            La Solución
          </span>
        </motion.div>
        <motion.h2 variants={fadeUp} className="text-3xl md:text-4xl lg:text-5xl font-black mb-6 leading-tight" style={{
        fontFamily: 'var(--font-heading)',
        color: '#FDF8F3'
      }} data-id="element-43">
          El Protocolo de Blindaje de Identidad
        </motion.h2>
        <motion.p variants={fadeUp} className="text-lg md:text-xl leading-relaxed" style={{
        color: 'rgba(253,248,243,0.9)',
        fontFamily: 'var(--font-body)'
      }} data-id="element-44">
          Este sistema separa tu vida en dos: la mujer que sana y la mujer que
          ejecuta. Mientras tú procesas el duelo, este manual actúa como tu{' '}
          <strong style={{
          color: '#FDF8F3'
        }} data-id="element-45">
            «Ejecutiva de Crisis»
          </strong>{' '}
          personal, dándote los pasos exactos para desvincularte legal y
          financieramente sin cometer errores fatales.
        </motion.p>
      </div>
    </AnimSection>;
}
// ─── 5. WHAT YOU GET ──────────────────────────────────────────────────────────
function WhatYouGetSection() {
  return <AnimSection style={{
    backgroundColor: 'var(--color-bg)'
  } as React.CSSProperties} data-id="element-46">
      <motion.div variants={fadeUp} className="text-center mb-12" data-id="element-47">
        <SectionLabel data-id="element-48">Contenido del Manual</SectionLabel>
        <h2 className="text-2xl md:text-3xl font-black" style={{
        fontFamily: 'var(--font-heading)',
        color: 'var(--color-text)'
      }} data-id="element-49">
          Lo que obtienes
        </h2>
      </motion.div>

      <motion.div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" variants={staggerContainer} data-id="element-50">
        {/* Card 1 */}
        <motion.div variants={fadeUp} className="flex flex-col gap-3 p-6 rounded-2xl shadow-sm border" style={{
        backgroundColor: '#FDF8F3',
        borderColor: '#EDD9CC'
      }} data-id="element-51">
          <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{
          backgroundColor: '#F5EDE6'
        }} data-id="element-52">
            <FileTextIcon className="w-5 h-5" style={{
            color: 'var(--color-primary)'
          }} data-id="element-53" />
          </div>
          <h3 className="font-bold text-base" style={{
          fontFamily: 'var(--font-heading)',
          color: 'var(--color-text)'
        }} data-id="element-54">
            Manual de Blindaje de 30 Días
          </h3>
          <p className="text-sm leading-relaxed" style={{
          color: 'var(--color-text-muted)',
          fontFamily: 'var(--font-body)'
        }} data-id="element-55">
            Hoja de ruta paso a paso desde la hora Cero.
          </p>
        </motion.div>

        {/* Card 2 */}
        <motion.div variants={fadeUp} className="flex flex-col gap-3 p-6 rounded-2xl shadow-sm border" style={{
        backgroundColor: '#FDF8F3',
        borderColor: '#EDD9CC'
      }} data-id="element-56">
          <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{
          backgroundColor: '#F5EDE6'
        }} data-id="element-57">
            <ShieldIcon className="w-5 h-5" style={{
            color: 'var(--color-primary)'
          }} data-id="element-58" />
          </div>
          <h3 className="font-bold text-base" style={{
          fontFamily: 'var(--font-heading)',
          color: 'var(--color-text)'
        }} data-id="element-59">
            Protocolo de Seguridad Digital
          </h3>
          <p className="text-sm leading-relaxed" style={{
          color: 'var(--color-text-muted)',
          fontFamily: 'var(--font-body)'
        }} data-id="element-60">
            Cómo evitar que rastreen tu ubicación o lean tus mensajes.
          </p>
        </motion.div>

        {/* Card 3 */}
        <motion.div variants={fadeUp} className="flex flex-col gap-3 p-6 rounded-2xl shadow-sm border" style={{
        backgroundColor: '#FDF8F3',
        borderColor: '#EDD9CC'
      }} data-id="element-61">
          <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{
          backgroundColor: '#F5EDE6'
        }} data-id="element-62">
            <CreditCardIcon className="w-5 h-5" style={{
            color: 'var(--color-primary)'
          }} data-id="element-63" />
          </div>
          <h3 className="font-bold text-base" style={{
          fontFamily: 'var(--font-heading)',
          color: 'var(--color-text)'
        }} data-id="element-64">
            Guía de Finanzas de Emergencia
          </h3>
          <p className="text-sm leading-relaxed" style={{
          color: 'var(--color-text-muted)',
          fontFamily: 'var(--font-body)'
        }} data-id="element-65">
            Cómo abrir cuentas y proteger tu crédito hoy mismo.
          </p>
        </motion.div>

        {/* Card 4 */}
        <motion.div variants={fadeUp} className="flex flex-col gap-3 p-6 rounded-2xl shadow-sm border" style={{
        backgroundColor: '#FDF8F3',
        borderColor: '#EDD9CC'
      }} data-id="element-66">
          <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{
          backgroundColor: '#F5EDE6'
        }} data-id="element-67">
            <MessageSquareIcon className="w-5 h-5" style={{
            color: 'var(--color-primary)'
          }} data-id="element-68" />
          </div>
          <h3 className="font-bold text-base" style={{
          fontFamily: 'var(--font-heading)',
          color: 'var(--color-text)'
        }} data-id="element-69">
            Scripts de Comunicación
          </h3>
          <p className="text-sm leading-relaxed" style={{
          color: 'var(--color-text-muted)',
          fontFamily: 'var(--font-body)'
        }} data-id="element-70">
            Qué decir (y qué NO decir) para que nada sea usado en tu contra en
            la corte.
          </p>
        </motion.div>

        {/* Card 5 */}
        <motion.div variants={fadeUp} className="flex flex-col gap-3 p-6 rounded-2xl shadow-sm border sm:col-span-2 lg:col-span-1" style={{
        backgroundColor: '#FDF8F3',
        borderColor: '#EDD9CC'
      }} data-id="element-71">
          <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{
          backgroundColor: '#F5EDE6'
        }} data-id="element-72">
            <BookOpenIcon className="w-5 h-5" style={{
            color: 'var(--color-primary)'
          }} data-id="element-73" />
          </div>
          <h3 className="font-bold text-base" style={{
          fontFamily: 'var(--font-heading)',
          color: 'var(--color-text)'
        }} data-id="element-74">
            Glosario Legal Español-Inglés
          </h3>
          <p className="text-sm leading-relaxed" style={{
          color: 'var(--color-text-muted)',
          fontFamily: 'var(--font-body)'
        }} data-id="element-75">
            Entiende exactamente qué estás firmando.
          </p>
        </motion.div>
      </motion.div>
    </AnimSection>;
}
// ─── 6. WHO IT'S FOR ──────────────────────────────────────────────────────────
function WhoForSection() {
  return <AnimSection style={{
    backgroundColor: '#F5EDE6'
  } as React.CSSProperties} data-id="element-76">
      <motion.div variants={fadeUp} className="text-center mb-12" data-id="element-77">
        <SectionLabel data-id="element-78">¿Es para mí?</SectionLabel>
        <h2 className="text-2xl md:text-3xl font-black" style={{
        fontFamily: 'var(--font-heading)',
        color: 'var(--color-text)'
      }} data-id="element-79">
          Esta guía es para ti
        </h2>
      </motion.div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-80">
        {/* For */}
        <motion.div variants={fadeUp} className="rounded-2xl p-7" style={{
        backgroundColor: '#FDF8F3',
        border: '1.5px solid #C4704A'
      }} data-id="element-81">
          <h3 className="font-bold text-lg mb-5 flex items-center gap-2" style={{
          fontFamily: 'var(--font-heading)',
          color: 'var(--color-primary)'
        }} data-id="element-82">
            <CheckIcon className="w-5 h-5" data-id="element-83" />
            Para ti si…
          </h3>
          <ul className="flex flex-col gap-4" data-id="element-84">
            <li className="flex items-start gap-3" data-id="element-85">
              <CheckIcon className="w-5 h-5 flex-shrink-0 mt-0.5" style={{
              color: '#4CAF50'
            }} data-id="element-86" />
              <span className="text-sm leading-relaxed" style={{
              color: 'var(--color-text)',
              fontFamily: 'var(--font-body)'
            }} data-id="element-87">
                Eres mujer latina en EE.&nbsp;UU. recientemente separada o en
                proceso de divorcio.
              </span>
            </li>
            <li className="flex items-start gap-3" data-id="element-88">
              <CheckIcon className="w-5 h-5 flex-shrink-0 mt-0.5" style={{
              color: '#4CAF50'
            }} data-id="element-89" />
              <span className="text-sm leading-relaxed" style={{
              color: 'var(--color-text)',
              fontFamily: 'var(--font-body)'
            }} data-id="element-90">
                Eres madre y necesitas proteger la estabilidad y rutina de tus
                hijos.
              </span>
            </li>
            <li className="flex items-start gap-3" data-id="element-91">
              <CheckIcon className="w-5 h-5 flex-shrink-0 mt-0.5" style={{
              color: '#4CAF50'
            }} data-id="element-92" />
              <span className="text-sm leading-relaxed" style={{
              color: 'var(--color-text)',
              fontFamily: 'var(--font-body)'
            }} data-id="element-93">
                Temes por tu estatus migratorio o estabilidad financiera.
              </span>
            </li>
            <li className="flex items-start gap-3" data-id="element-94">
              <CheckIcon className="w-5 h-5 flex-shrink-0 mt-0.5" style={{
              color: '#4CAF50'
            }} data-id="element-95" />
              <span className="text-sm leading-relaxed" style={{
              color: 'var(--color-text)',
              fontFamily: 'var(--font-body)'
            }} data-id="element-96">
                Te sientes abrumada por la burocracia del sistema americano.
              </span>
            </li>
          </ul>
        </motion.div>

        {/* Not for */}
        <motion.div variants={fadeUp} className="rounded-2xl p-7" style={{
        backgroundColor: '#FDF8F3',
        border: '1.5px solid #D4B8AC'
      }} data-id="element-97">
          <h3 className="font-bold text-lg mb-5 flex items-center gap-2" style={{
          fontFamily: 'var(--font-heading)',
          color: 'var(--color-text-muted)'
        }} data-id="element-98">
            <XIcon className="w-5 h-5" data-id="element-99" />
            No es para ti si…
          </h3>
          <ul className="flex flex-col gap-4" data-id="element-100">
            <li className="flex items-start gap-3" data-id="element-101">
              <XIcon className="w-5 h-5 flex-shrink-0 mt-0.5" style={{
              color: 'var(--color-primary)'
            }} data-id="element-102" />
              <span className="text-sm leading-relaxed" style={{
              color: 'var(--color-text-muted)',
              fontFamily: 'var(--font-body)'
            }} data-id="element-103">
                Buscas terapia de pareja o reconciliación.
              </span>
            </li>
            <li className="flex items-start gap-3" data-id="element-104">
              <XIcon className="w-5 h-5 flex-shrink-0 mt-0.5" style={{
              color: 'var(--color-primary)'
            }} data-id="element-105" />
              <span className="text-sm leading-relaxed" style={{
              color: 'var(--color-text-muted)',
              fontFamily: 'var(--font-body)'
            }} data-id="element-106">
                No estás dispuesta a tomar acciones prácticas e inmediatas.
              </span>
            </li>
            <li className="flex items-start gap-3" data-id="element-107">
              <XIcon className="w-5 h-5 flex-shrink-0 mt-0.5" style={{
              color: 'var(--color-primary)'
            }} data-id="element-108" />
              <span className="text-sm leading-relaxed" style={{
              color: 'var(--color-text-muted)',
              fontFamily: 'var(--font-body)'
            }} data-id="element-109">
                Buscas asesoría legal gratuita — esto es una guía de estrategia
                y logística.
              </span>
            </li>
          </ul>
        </motion.div>
      </div>
    </AnimSection>;
}
// ─── 7. HOW IT WORKS ──────────────────────────────────────────────────────────
function HowItWorksSection() {
  return <AnimSection style={{
    backgroundColor: 'var(--color-bg-dark)'
  } as React.CSSProperties} data-id="element-110">
      <motion.div variants={fadeUp} className="text-center mb-14" data-id="element-111">
        <SectionLabel data-id="element-112">El Proceso</SectionLabel>
        <h2 className="text-2xl md:text-3xl font-black" style={{
        fontFamily: 'var(--font-heading)',
        color: '#FDF8F3'
      }} data-id="element-113">
          Cómo funciona
        </h2>
      </motion.div>

      <motion.div className="grid grid-cols-1 md:grid-cols-3 gap-8" variants={staggerContainer} data-id="element-114">
        {/* Step 1 */}
        <motion.div variants={fadeUp} className="flex flex-col items-center text-center gap-4" data-id="element-115">
          <span className="text-7xl font-black leading-none" style={{
          fontFamily: 'var(--font-heading)',
          color: 'var(--color-primary)'
        }} data-id="element-116">
            01
          </span>
          <h3 className="text-xl font-bold" style={{
          fontFamily: 'var(--font-heading)',
          color: '#FDF8F3'
        }} data-id="element-117">
            Descarga
          </h3>
          <p className="text-sm leading-relaxed" style={{
          color: '#D4B8AC',
          fontFamily: 'var(--font-body)'
        }} data-id="element-118">
            Obtén acceso inmediato al manual en PDF.
          </p>
        </motion.div>

        {/* Step 2 */}
        <motion.div variants={fadeUp} className="flex flex-col items-center text-center gap-4" data-id="element-119">
          <span className="text-7xl font-black leading-none" style={{
          fontFamily: 'var(--font-heading)',
          color: 'var(--color-primary)'
        }} data-id="element-120">
            02
          </span>
          <h3 className="text-xl font-bold" style={{
          fontFamily: 'var(--font-heading)',
          color: '#FDF8F3'
        }} data-id="element-121">
            Blinda
          </h3>
          <p className="text-sm leading-relaxed" style={{
          color: '#D4B8AC',
          fontFamily: 'var(--font-body)'
        }} data-id="element-122">
            Ejecuta las acciones de las primeras 48 horas para asegurar tu
            dinero y privacidad.
          </p>
        </motion.div>

        {/* Step 3 */}
        <motion.div variants={fadeUp} className="flex flex-col items-center text-center gap-4" data-id="element-123">
          <span className="text-7xl font-black leading-none" style={{
          fontFamily: 'var(--font-heading)',
          color: 'var(--color-primary)'
        }} data-id="element-124">
            03
          </span>
          <h3 className="text-xl font-bold" style={{
          fontFamily: 'var(--font-heading)',
          color: '#FDF8F3'
        }} data-id="element-125">
            Escala
          </h3>
          <p className="text-sm leading-relaxed" style={{
          color: '#D4B8AC',
          fontFamily: 'var(--font-body)'
        }} data-id="element-126">
            Sigue la hoja de ruta de 30 días para reconstruir tu presupuesto y
            tu nueva vida.
          </p>
        </motion.div>
      </motion.div>
    </AnimSection>;
}
// ─── 8. PROOF ─────────────────────────────────────────────────────────────────
function ProofSection() {
  return <AnimSection style={{
    backgroundColor: 'var(--color-bg)'
  } as React.CSSProperties} data-id="element-127">
      <motion.div variants={fadeUp} className="max-w-3xl mx-auto text-center" data-id="element-128">
        <SectionLabel data-id="element-129">Lo que cambia</SectionLabel>
        <h2 className="text-2xl md:text-3xl font-black mb-10" style={{
        fontFamily: 'var(--font-heading)',
        color: 'var(--color-text)'
      }} data-id="element-130">
          En 7 días…
        </h2>
        <div className="relative rounded-2xl p-8 md:p-12" style={{
        backgroundColor: '#F5EDE6',
        borderLeft: '4px solid var(--color-accent)'
      }} data-id="element-131">
          <span className="absolute top-4 left-6 text-6xl leading-none font-black opacity-20" style={{
          color: 'var(--color-accent)',
          fontFamily: 'var(--font-heading)'
        }} aria-hidden="true" data-id="element-132">
            "
          </span>
          <p className="text-lg md:text-xl leading-relaxed relative z-10" style={{
          color: 'var(--color-text)',
          fontFamily: 'var(--font-body)'
        }} data-id="element-133">
            En los próximos 7 días, pasarás de despertarte con taquicardia por
            la incertidumbre a tener una carpeta organizada con cada documento,
            clave y decisión financiera{' '}
            <strong data-id="element-134">bajo tu control absoluto.</strong> No es magia, es
            logística.
          </p>
          <span className="absolute bottom-2 right-6 text-6xl leading-none font-black opacity-20" style={{
          color: 'var(--color-accent)',
          fontFamily: 'var(--font-heading)'
        }} aria-hidden="true" data-id="element-135">
            "
          </span>
        </div>
      </motion.div>
    </AnimSection>;
}
// ─── 9. FAQ ───────────────────────────────────────────────────────────────────
type FaqItem = {
  q: string;
  a: string;
};
function FaqAccordion({
  items
}: {
  items: FaqItem[];
}) {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  return <div className="flex flex-col gap-3" data-id="element-136">
      {items.map((item, i) => {
      const isOpen = openIndex === i;
      return <div key={i} className="rounded-xl overflow-hidden border" style={{
        borderColor: isOpen ? 'var(--color-primary)' : '#EDD9CC'
      }} data-id="element-137">
            <button className="w-full flex items-center justify-between px-6 py-5 text-left transition-colors duration-200" style={{
          backgroundColor: isOpen ? '#F5EDE6' : '#FDF8F3',
          fontFamily: 'var(--font-body)'
        }} onClick={() => setOpenIndex(isOpen ? null : i)} aria-expanded={isOpen} data-id="element-138">
              <span className="font-bold text-base pr-4" style={{
            color: 'var(--color-text)'
          }} data-id="element-139">
                {item.q}
              </span>
              <motion.span animate={{
            rotate: isOpen ? 180 : 0
          }} transition={{
            duration: 0.25
          }} className="flex-shrink-0" data-id="element-140">
                <ChevronDownIcon className="w-5 h-5" style={{
              color: 'var(--color-primary)'
            }} data-id="element-141" />
              </motion.span>
            </button>
            <AnimatePresence initial={false} data-id="element-142">
              {isOpen && <motion.div key="content" initial={{
            height: 0,
            opacity: 0
          }} animate={{
            height: 'auto',
            opacity: 1
          }} exit={{
            height: 0,
            opacity: 0
          }} transition={{
            duration: 0.3,
            ease: 'easeInOut'
          }} style={{
            overflow: 'hidden'
          }} data-id="element-143">
                  <div className="px-6 pb-5 pt-1 text-sm leading-relaxed" style={{
              color: 'var(--color-text-muted)',
              fontFamily: 'var(--font-body)'
            }} data-id="element-144">
                    {item.a}
                  </div>
                </motion.div>}
            </AnimatePresence>
          </div>;
    })}
    </div>;
}
function FaqSection() {
  const faqs: FaqItem[] = [{
    q: '¿Necesito un abogado para usar esta guía?',
    a: 'No, esta guía te prepara para que, cuando hables con un abogado, no pierdas tiempo ni dinero y sepas exactamente qué exigir.'
  }, {
    q: '¿Sirve si mi ex todavía vive en la casa?',
    a: 'Sí, incluye un protocolo específico para convivencia forzada y protección de documentos.'
  }, {
    q: '¿Es seguro comprar desde mi celular?',
    a: 'Totalmente. El acceso es privado y discreto.'
  }, {
    q: '¿Qué pasa si no sé mucho inglés?',
    a: 'La guía incluye un glosario y explicaciones claras de los términos que verás en los documentos oficiales de EE. UU.'
  }];
  return <AnimSection style={{
    backgroundColor: '#F5EDE6'
  } as React.CSSProperties} data-id="element-145">
      <motion.div variants={fadeUp} className="text-center mb-12" data-id="element-146">
        <SectionLabel data-id="element-147">Dudas Comunes</SectionLabel>
        <h2 className="text-2xl md:text-3xl font-black" style={{
        fontFamily: 'var(--font-heading)',
        color: 'var(--color-text)'
      }} data-id="element-148">
          Preguntas Frecuentes
        </h2>
      </motion.div>
      <motion.div variants={fadeUp} className="max-w-2xl mx-auto" data-id="element-149">
        <FaqAccordion items={faqs} data-id="element-150" />
      </motion.div>
    </AnimSection>;
}
// ─── 10. OFFER + CTA ──────────────────────────────────────────────────────────
function OfferSection() {
  return <AnimSection id="oferta" style={{
    backgroundColor: 'var(--color-primary)'
  } as React.CSSProperties} data-id="element-151">
      <motion.div variants={fadeUp} className="text-center max-w-2xl mx-auto flex flex-col gap-6" data-id="element-152">
        <p className="text-xs font-bold tracking-[0.25em] uppercase" style={{
        color: 'rgba(253,248,243,0.7)',
        fontFamily: 'var(--font-body)'
      }} data-id="element-153">
          Oferta de Acceso Inmediato
        </p>

        <h2 className="text-3xl md:text-4xl font-black leading-tight" style={{
        fontFamily: 'var(--font-heading)',
        color: '#FDF8F3'
      }} data-id="element-154">
          No dejes que el pánico te cueste tu futuro.
        </h2>

        <p className="text-base md:text-lg leading-relaxed" style={{
        color: 'rgba(253,248,243,0.85)',
        fontFamily: 'var(--font-body)'
      }} data-id="element-155">
          Por menos de lo que cuesta un café, obtén la hoja de ruta que te
          ahorrará miles de dólares en errores legales y financieros.
        </p>

        {/* Price block */}
        <div className="rounded-2xl py-8 px-6 flex flex-col items-center gap-2" style={{
        backgroundColor: 'rgba(0,0,0,0.15)'
      }} data-id="element-156">
          <p className="text-sm font-bold tracking-widest uppercase" style={{
          color: 'rgba(253,248,243,0.7)',
          fontFamily: 'var(--font-body)'
        }} data-id="element-157">
            Acceso Inmediato
          </p>
          <div className="flex items-end gap-2" data-id="element-158">
            <span className="text-7xl font-black leading-none" style={{
            fontFamily: 'var(--font-heading)',
            color: '#FDF8F3'
          }} data-id="element-159">
              $6.39
            </span>
            <span className="text-xl font-bold pb-2" style={{
            color: 'rgba(253,248,243,0.7)',
            fontFamily: 'var(--font-body)'
          }} data-id="element-160">
              USD
            </span>
          </div>
          <p className="text-xs" style={{
          color: 'rgba(253,248,243,0.6)',
          fontFamily: 'var(--font-body)'
        }} data-id="element-161">
            Pago único · Descarga instantánea
          </p>
        </div>

        <CtaButton variant="inverse" data-id="element-162" />

        <p className="flex items-center justify-center gap-2 text-sm" style={{
        color: 'rgba(253,248,243,0.75)',
        fontFamily: 'var(--font-body)'
      }} data-id="element-163">
          <LockIcon className="w-4 h-4" data-id="element-164" />
          Tu pago es seguro y la descarga es instantánea y discreta.
        </p>
      </motion.div>
    </AnimSection>;
}
// ─── LEGAL MODAL ─────────────────────────────────────────────────────────────
type ModalType = 'privacidad' | 'terminos' | null;
function LegalModal({
  type,
  onClose
}: {
  type: ModalType;
  onClose: () => void;
}) {
  if (!type) return null;
  const isPrivacy = type === 'privacidad';
  const privacyContent = <>
      <p className="text-sm leading-relaxed mb-4" style={{
      color: 'var(--color-text-muted)',
      fontFamily: 'var(--font-body)'
    }} data-id="element-165">
        Última actualización: {new Date().getFullYear()}
      </p>
      <p className="leading-relaxed mb-5" style={{
      fontFamily: 'var(--font-body)',
      color: 'var(--color-text)'
    }} data-id="element-166">
        Tu privacidad es importante para nosotros. Esta política explica cómo
        recopilamos, usamos y protegemos tu información personal cuando
        adquieres o utilizas la{' '}
        <strong data-id="element-167">Guía de Supervivencia Post-Divorcio</strong>.
      </p>
      <h3 className="text-lg font-bold mb-2" style={{
      fontFamily: 'var(--font-heading)',
      color: 'var(--color-text)'
    }} data-id="element-168">
        1. Información que recopilamos
      </h3>
      <p className="leading-relaxed mb-5" style={{
      fontFamily: 'var(--font-body)',
      color: 'var(--color-text)'
    }} data-id="element-169">
        Al realizar tu compra, el procesador de pagos puede recopilar tu nombre,
        dirección de correo electrónico e información de pago. Nosotros
        únicamente recibimos la confirmación de la transacción y tu correo
        electrónico para enviarte el acceso al producto.
      </p>
      <h3 className="text-lg font-bold mb-2" style={{
      fontFamily: 'var(--font-heading)',
      color: 'var(--color-text)'
    }} data-id="element-170">
        2. Uso de la información
      </h3>
      <p className="leading-relaxed mb-5" style={{
      fontFamily: 'var(--font-body)',
      color: 'var(--color-text)'
    }} data-id="element-171">
        Utilizamos tu información exclusivamente para: (a) entregar el producto
        digital adquirido, (b) enviarte actualizaciones relacionadas con el
        producto si así lo autorizas, y (c) cumplir con obligaciones legales
        aplicables.
      </p>
      <h3 className="text-lg font-bold mb-2" style={{
      fontFamily: 'var(--font-heading)',
      color: 'var(--color-text)'
    }} data-id="element-172">
        3. Compartir información
      </h3>
      <p className="leading-relaxed mb-5" style={{
      fontFamily: 'var(--font-body)',
      color: 'var(--color-text)'
    }} data-id="element-173">
        No vendemos, alquilamos ni compartimos tu información personal con
        terceros con fines comerciales. Solo compartimos datos con proveedores
        de servicios estrictamente necesarios para procesar tu pago y entregar
        el producto (por ejemplo, plataformas de pago seguro).
      </p>
      <h3 className="text-lg font-bold mb-2" style={{
      fontFamily: 'var(--font-heading)',
      color: 'var(--color-text)'
    }} data-id="element-174">
        4. Seguridad
      </h3>
      <p className="leading-relaxed mb-5" style={{
      fontFamily: 'var(--font-body)',
      color: 'var(--color-text)'
    }} data-id="element-175">
        Implementamos medidas de seguridad razonables para proteger tu
        información. Las transacciones de pago se procesan a través de
        plataformas cifradas y seguras. No almacenamos datos de tarjetas de
        crédito en nuestros sistemas.
      </p>
      <h3 className="text-lg font-bold mb-2" style={{
      fontFamily: 'var(--font-heading)',
      color: 'var(--color-text)'
    }} data-id="element-176">
        5. Tus derechos
      </h3>
      <p className="leading-relaxed mb-5" style={{
      fontFamily: 'var(--font-body)',
      color: 'var(--color-text)'
    }} data-id="element-177">
        Tienes derecho a acceder, corregir o solicitar la eliminación de tu
        información personal. Para ejercer estos derechos, puedes comunicarte
        con nosotros a través de los canales disponibles en la plataforma de
        compra.
      </p>
      <h3 className="text-lg font-bold mb-2" style={{
      fontFamily: 'var(--font-heading)',
      color: 'var(--color-text)'
    }} data-id="element-178">
        6. Cookies
      </h3>
      <p className="leading-relaxed mb-5" style={{
      fontFamily: 'var(--font-body)',
      color: 'var(--color-text)'
    }} data-id="element-179">
        Esta página puede utilizar cookies técnicas necesarias para su
        funcionamiento. No utilizamos cookies de rastreo publicitario sin tu
        consentimiento.
      </p>
      <h3 className="text-lg font-bold mb-2" style={{
      fontFamily: 'var(--font-heading)',
      color: 'var(--color-text)'
    }} data-id="element-180">
        7. Cambios a esta política
      </h3>
      <p className="leading-relaxed" style={{
      fontFamily: 'var(--font-body)',
      color: 'var(--color-text)'
    }} data-id="element-181">
        Nos reservamos el derecho de actualizar esta política en cualquier
        momento. Los cambios serán publicados en esta misma página con la fecha
        de actualización correspondiente.
      </p>
    </>;
  const termsContent = <>
      <p className="text-sm leading-relaxed mb-4" style={{
      color: 'var(--color-text-muted)',
      fontFamily: 'var(--font-body)'
    }} data-id="element-182">
        Última actualización: {new Date().getFullYear()}
      </p>
      <p className="leading-relaxed mb-5" style={{
      fontFamily: 'var(--font-body)',
      color: 'var(--color-text)'
    }} data-id="element-183">
        Al adquirir y utilizar la{' '}
        <strong data-id="element-184">
          Guía de Supervivencia Post-Divorcio: El Protocolo de 30 Días para
          Recuperar el Control
        </strong>
        , aceptas los siguientes términos y condiciones.
      </p>
      <h3 className="text-lg font-bold mb-2" style={{
      fontFamily: 'var(--font-heading)',
      color: 'var(--color-text)'
    }} data-id="element-185">
        1. Naturaleza del producto
      </h3>
      <p className="leading-relaxed mb-5" style={{
      fontFamily: 'var(--font-body)',
      color: 'var(--color-text)'
    }} data-id="element-186">
        Este producto es una guía digital de carácter informativo, educativo y
        estratégico.{' '}
        <strong data-id="element-187">No constituye asesoría legal, financiera ni migratoria.</strong>{' '}
        La información contenida no reemplaza la consulta con un abogado
        licenciado u otro profesional calificado.
      </p>
      <h3 className="text-lg font-bold mb-2" style={{
      fontFamily: 'var(--font-heading)',
      color: 'var(--color-text)'
    }} data-id="element-188">
        2. Licencia de uso
      </h3>
      <p className="leading-relaxed mb-5" style={{
      fontFamily: 'var(--font-body)',
      color: 'var(--color-text)'
    }} data-id="element-189">
        Al adquirir este producto, obtienes una licencia personal, no
        transferible y no exclusiva para uso individual. Queda estrictamente
        prohibido reproducir, distribuir, revender, compartir o publicar el
        contenido de esta guía, total o parcialmente, sin autorización escrita
        previa.
      </p>
      <h3 className="text-lg font-bold mb-2" style={{
      fontFamily: 'var(--font-heading)',
      color: 'var(--color-text)'
    }} data-id="element-190">
        3. Política de reembolso
      </h3>
      <p className="leading-relaxed mb-5" style={{
      fontFamily: 'var(--font-body)',
      color: 'var(--color-text)'
    }} data-id="element-191">
        Dado que se trata de un producto digital de descarga inmediata, no se
        ofrecen reembolsos una vez que el acceso ha sido entregado. Si
        experimentas un problema técnico con la descarga, comunícate a través de
        la plataforma de compra para recibir asistencia.
      </p>
      <h3 className="text-lg font-bold mb-2" style={{
      fontFamily: 'var(--font-heading)',
      color: 'var(--color-text)'
    }} data-id="element-192">
        4. Limitación de responsabilidad
      </h3>
      <p className="leading-relaxed mb-5" style={{
      fontFamily: 'var(--font-body)',
      color: 'var(--color-text)'
    }} data-id="element-193">
        Los resultados descritos en esta guía son ilustrativos y no
        garantizados. Los resultados individuales pueden variar según las
        circunstancias personales de cada usuaria. No nos hacemos responsables
        por decisiones tomadas con base en el contenido de esta guía sin la
        supervisión de un profesional calificado.
      </p>
      <h3 className="text-lg font-bold mb-2" style={{
      fontFamily: 'var(--font-heading)',
      color: 'var(--color-text)'
    }} data-id="element-194">
        5. Propiedad intelectual
      </h3>
      <p className="leading-relaxed mb-5" style={{
      fontFamily: 'var(--font-body)',
      color: 'var(--color-text)'
    }} data-id="element-195">
        Todo el contenido de esta guía — incluyendo textos, estructura,
        metodología y materiales — está protegido por derechos de autor.
        Cualquier uso no autorizado constituye una violación de estos derechos y
        podrá dar lugar a acciones legales.
      </p>
      <h3 className="text-lg font-bold mb-2" style={{
      fontFamily: 'var(--font-heading)',
      color: 'var(--color-text)'
    }} data-id="element-196">
        6. Ley aplicable
      </h3>
      <p className="leading-relaxed" style={{
      fontFamily: 'var(--font-body)',
      color: 'var(--color-text)'
    }} data-id="element-197">
        Estos términos se rigen por las leyes aplicables en el lugar de
        residencia del proveedor del servicio. Cualquier disputa será resuelta
        de buena fe entre las partes antes de recurrir a instancias legales.
      </p>
    </>;
  return <AnimatePresence data-id="element-198">
      <motion.div className="fixed inset-0 z-50 flex items-center justify-center p-4" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.2
    }} data-id="element-199">
        {/* Backdrop */}
        <div className="absolute inset-0" style={{
        backgroundColor: 'rgba(44,24,16,0.7)',
        backdropFilter: 'blur(4px)'
      }} onClick={onClose} data-id="element-200" />

        {/* Modal panel */}
        <motion.div className="relative w-full max-w-2xl max-h-[85vh] rounded-2xl overflow-hidden flex flex-col shadow-2xl" style={{
        backgroundColor: 'var(--color-bg)'
      }} initial={{
        opacity: 0,
        y: 24,
        scale: 0.97
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: 24,
        scale: 0.97
      }} transition={{
        duration: 0.25,
        ease: 'easeOut'
      }} data-id="element-201">
          {/* Header */}
          <div className="flex items-center justify-between px-7 py-5 border-b flex-shrink-0" style={{
          borderColor: '#EDD9CC',
          backgroundColor: '#FDF8F3'
        }} data-id="element-202">
            <h2 className="text-xl font-black" style={{
            fontFamily: 'var(--font-heading)',
            color: 'var(--color-text)'
          }} data-id="element-203">
              {isPrivacy ? 'Política de Privacidad' : 'Términos y Condiciones'}
            </h2>
            <button onClick={onClose} className="flex items-center justify-center w-8 h-8 rounded-full transition-colors duration-150 hover:bg-[#F5EDE6]" aria-label="Cerrar" data-id="element-204">
              <XCircleIcon className="w-5 h-5" style={{
              color: 'var(--color-text-muted)'
            }} data-id="element-205" />
            </button>
          </div>

          {/* Scrollable content */}
          <div className="overflow-y-auto px-7 py-6 flex-1 text-base" data-id="element-206">
            {isPrivacy ? privacyContent : termsContent}
          </div>

          {/* Footer */}
          <div className="px-7 py-4 border-t flex-shrink-0 text-center" style={{
          borderColor: '#EDD9CC',
          backgroundColor: '#FDF8F3'
        }} data-id="element-207">
            <button onClick={onClose} className="text-sm font-bold px-6 py-2 rounded-full transition-colors duration-150" style={{
            backgroundColor: 'var(--color-primary)',
            color: '#FDF8F3',
            fontFamily: 'var(--font-body)'
          }} data-id="element-208">
              Cerrar
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>;
}
// ─── FOOTER ───────────────────────────────────────────────────────────────────
function Footer() {
  const [modal, setModal] = useState<ModalType>(null);
  return <>
      <LegalModal type={modal} onClose={() => setModal(null)} data-id="element-209" />
      <footer className="w-full py-8 px-5 text-center flex flex-col items-center gap-3" style={{
      backgroundColor: 'var(--color-bg-dark)'
    }} data-id="element-210">
        <div className="flex items-center gap-6" data-id="element-211">
          <button onClick={() => setModal('privacidad')} className="text-xs underline underline-offset-2 transition-colors duration-150 hover:opacity-80" style={{
          color: 'rgba(212,184,172,0.7)',
          fontFamily: 'var(--font-body)'
        }} data-id="element-212">
            Política de Privacidad
          </button>
          <span style={{
          color: 'rgba(212,184,172,0.3)'
        }} data-id="element-213">
            ·
          </span>
          <button onClick={() => setModal('terminos')} className="text-xs underline underline-offset-2 transition-colors duration-150 hover:opacity-80" style={{
          color: 'rgba(212,184,172,0.7)',
          fontFamily: 'var(--font-body)'
        }} data-id="element-214">
            Términos y Condiciones
          </button>
        </div>
        <p className="text-xs" style={{
        color: 'rgba(212,184,172,0.4)',
        fontFamily: 'var(--font-body)'
      }} data-id="element-215">
          © {new Date().getFullYear()} Guía de Supervivencia Post-Divorcio.
          Esta guía es para fines informativos y educativos únicamente. No
          constituye asesoría legal.
        </p>
      </footer>
    </>;
}
// ─── APP ──────────────────────────────────────────────────────────────────────
export function App() {
  return <div className="w-full min-h-screen" style={{
    backgroundColor: 'var(--color-bg)'
  }} data-id="element-216">
      <HeroSection data-id="element-217" />
      <ProblemSection data-id="element-218" />
      <WhyNotWorkedSection data-id="element-219" />
      <MechanismSection data-id="element-220" />
      <WhatYouGetSection data-id="element-221" />
      <WhoForSection data-id="element-222" />
      <HowItWorksSection data-id="element-223" />
      <ProofSection data-id="element-224" />
      <FaqSection data-id="element-225" />
      <OfferSection data-id="element-226" />
      <Footer data-id="element-227" />
    </div>;
}